import { actionLabel } from '../actionLabels.js'
import type { AuditEntry } from '../types.js'

interface AuditLogListProps {
  entries: AuditEntry[]
}

function formatTime(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function actorLabel(entry: AuditEntry): string {
  if (entry.actor_type === 'system') return 'system'
  if (entry.actor_type === 'impersonating_admin') {
    return `${entry.admin_email ?? 'admin'} (as ${entry.acting_as_user_email ?? 'owner'})`
  }
  return entry.admin_email ?? 'admin'
}

/** Pulls a short human description out of an entry's metadata, if useful. */
function describe(entry: AuditEntry): string | null {
  const m = entry.metadata
  if (typeof m['reason'] === 'string') return m['reason']
  if (typeof m['new_plan_tier'] === 'string') {
    return `${String(m['previous_plan_tier'] ?? '?')} → ${m['new_plan_tier']}`
  }
  if (typeof m['inner_action'] === 'string') {
    return actionLabel(m['inner_action'])
  }
  return null
}

export function AuditLogList({ entries }: AuditLogListProps) {
  if (entries.length === 0) {
    return (
      <p className="admin-audit-meta" data-testid="audit-empty">
        No activity recorded yet.
      </p>
    )
  }

  return (
    <div data-testid="audit-list">
      {entries.map((entry) => {
        const desc = describe(entry)
        return (
          <div key={entry.id} className="admin-audit-row">
            <div className="admin-audit-time">{formatTime(entry.created_at)}</div>
            <div>
              <span className="admin-audit-action">
                {actionLabel(entry.action)}
              </span>
              <span className="admin-audit-meta"> · {actorLabel(entry)}</span>
              {desc && <div className="admin-audit-meta">{desc}</div>}
            </div>
          </div>
        )
      })}
    </div>
  )
}
